import cx from 'clsx';
import React from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as DoubleArrowIcon } from '@assets/svg/double-arrow.svg';
import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import useTranslation from '@hooks/useTranslation';

import type { ITableSorting } from '@model/tableSorting';

import { totalStatisticsItems } from '@pages/SiteEngagement/constants/totalStatisticsItems';
import type { IEngagementStatsFacility } from '@pages/SiteEngagement/model/engagementStatsFacility';

import {
  selectEngagementFilterShowFullTotalStatistics,
  useEngagementFilterActions,
} from '../../store/useEngagementFilterStore';
import TableTotalStatisticsSkeleton from './TableTotalStatisticsSkeleton';
import style from './style.module.scss';

interface IProps {
  data: IEngagementStatsFacility | undefined;
  isLoading: boolean;
  sorting: ITableSorting;
  onChangeSorting: (field: string) => void;
}

const TableTotalStatistics: React.FC<IProps> = ({ data, isLoading, sorting, onChangeSorting }) => {
  const { t } = useTranslation();

  const showFullTotalStatistics = selectEngagementFilterShowFullTotalStatistics();
  const { setEngagementFilterShowFullTotalStatistics } = useEngagementFilterActions();

  const TOOLTIP_ID = 'total-statistics-table-sort-tooltip';
  const LONG_TEXT_TOOLTIP_ID = 'total-statistics-long-text-tooltip';

  return (
    <>
      <div className={style.totalStatisticsContainer}>
        <div className={style.totalStatisticsLeftContainer}>
          <div className={style.totalStatisticsContainerTitle}>{t('Total Statistics (Averages)')}</div>
          <div className={style.totalStatisticsValues}>
            {totalStatisticsItems.map((item) => (
              <div
                key={item.dataKey}
                className={style.totalStatisticsStat}
                data-tooltip-content={t(item.tooltipText)}
                data-tooltip-id={LONG_TEXT_TOOLTIP_ID}
              >
                <div className={style.totalStatisticsStatCount}>{data?.total[item.dataKey] ?? '-'}</div>
                <div className={style.totalStatisticsStatLabel}>{`${t(item.label)} (${t(item.unit)})`}</div>
              </div>
            ))}
          </div>
        </div>
        <DoubleArrowIcon
          className={cx(style.arrowIcon, {
            [style.openedArrowIcon]: showFullTotalStatistics,
          })}
          onClick={() => setEngagementFilterShowFullTotalStatistics(!showFullTotalStatistics)}
          data-tooltip-content={
            showFullTotalStatistics ? t('Hide Facility Statistics') : t('Show Facility Statistics')
          }
          data-tooltip-id={LONG_TEXT_TOOLTIP_ID}
        />
      </div>
      <div
        className={cx(style.totalStatisticsTableWrapper, {
          [style.hideTotalStatisticTable]: !showFullTotalStatistics,
        })}
      >
        <table className={style.totalStatisticsTable}>
          <thead>
            <tr>
              <th className={style.columnLeft}>
                {t('Facility')}
                <TableSort
                  className={style.totalStatisticsTableSortIcon}
                  onClick={() => onChangeSorting('facility_name')}
                  data-tooltip-content="facility_name"
                  data-tooltip-id={TOOLTIP_ID}
                />
              </th>
              <th>
                <span
                  data-tooltip-content={t(
                    'The number of days since the action item was created. Note that the date range beneath the day count represents the creation date and the date of the latest update/completion report.',
                  )}
                  data-tooltip-id={LONG_TEXT_TOOLTIP_ID}
                >
                  {t('Age')} ({t('Days')})
                </span>
              </th>
              <th>
                <span
                  data-tooltip-content={t(
                    'The total number of days during which Waites was awaiting a response from the site (that is, the days the action item was in the “New“, “Needs Response“, and “Awaiting Update“ statuses).',
                  )}
                  data-tooltip-id={LONG_TEXT_TOOLTIP_ID}
                >
                  {t('Time Awaiting Site Response')} ({t('Days')})
                </span>
              </th>
              <th>
                <span
                  className={style.ellipsisTextThreeLines}
                  data-tooltip-content={t(
                    'The average number of days the action item was in the “New“, “Needs Response“, or “Awaiting Update“ statuses before a customer response or completion report.',
                  )}
                  data-tooltip-id={LONG_TEXT_TOOLTIP_ID}
                >
                  {t('Site response')} ({t('Days')})
                </span>
              </th>
              <th>
                <span
                  data-tooltip-content={t(
                    'The number of days the action item was in the “New” status before there was a response.',
                  )}
                  data-tooltip-id={LONG_TEXT_TOOLTIP_ID}
                >
                  {t('Initial response')} ({t('Days')})
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <TableTotalStatisticsSkeleton />
            ) : data?.facilities.length ? (
              data?.facilities.map((row) => (
                <tr key={row.facility_id} className={style.totalStatisticsBodyRow}>
                  <td className={style.columnLeft}>{row.facility_name}</td>
                  <td>{row.avg_age}</td>
                  <td>{row.avg_customer_response}</td>
                  <td>{row.avg_customer_response_average}</td>
                  <td>{row.avg_days_after_first_customer_reaction}</td>
                </tr>
              ))
            ) : (
              <TableEmptyRowExtended text={t('No facilities were found.')} />
            )}
          </tbody>
        </table>
        <TooltipSort id={TOOLTIP_ID} sorting={sorting} />
      </div>
      <Tooltip id={LONG_TEXT_TOOLTIP_ID} className={style.tooltipSizeMedium} />
    </>
  );
};

export default TableTotalStatistics;
